/**
 * Invoice addressee resolution (OWA-320).
 *
 * A case may designate an optional per-case bill-to contact (e.g. a third-party
 * payer such as Texas Legal). When present, that contact replaces the client(s)
 * as the invoice's addressee — changing both the name and the address. These two
 * helpers isolate that decision so it can be unit-tested without pdfmake:
 *
 *   - resolveDraftAddressees  — live (draft) path: returns the Contact(s) to address.
 *   - resolveFrozenAddressee  — finalized path: reads the frozen display snapshot.
 *
 * Both fall back to the client(s) when no bill-to is set (or, for the draft path,
 * when the bill-to contact can no longer be resolved — e.g. it was deleted).
 */
import find from '@/store/find';
import type Contact from '@/entities/contact';
import type Matter from '@/entities/matter';
import type {FinalDisplayMetadata} from '@/@types/invoice';

/**
 * Live (draft) addressee resolution: the bill-to contact when the matter
 * designates one and it resolves, otherwise the invoice's client(s).
 */
export function resolveDraftAddressees(clients: Contact[], matter: Matter | null): Contact[] {
    const billToContact = matter?.billToContactId
        ? find<Contact>('contacts', matter.billToContactId)
        : null;
    return billToContact ? [billToContact] : clients;
}

/**
 * Finalized addressee resolution from the frozen display snapshot: the bill-to
 * name + address when present, otherwise the joined client name(s) and the first
 * client address.
 */
export function resolveFrozenAddressee(
    display: FinalDisplayMetadata
): {name: string; address: string | null} {
    const billTo = display.billTo;
    if (billTo) {
        return {name: billTo.name, address: billTo.address};
    }
    return {
        name: display.clients.map(c => c.billingName || c.name).join(', '),
        address: display.clients.find(c => c.address)?.address || null,
    };
}
